import { useCallback } from "react";

import { type Trade, type TradeQueryParams, TradeQueryParamsSchema, tradesEndpoints } from "../../../api";
import { useCachedStrapiList, type UseCachedStrapiListOptions } from "../../../hooks/strapi";
import getErrorMessage from "../../../utils/getErrorMessage";
import handleError from "../../utils/handleError";

type UseTradesByBotProps = Omit<UseCachedStrapiListOptions<Trade>, "cacheKey" | "depsToReset">;

const useTradesByBot = (botId: number | string | undefined, props?: UseTradesByBotProps) => {
  const cacheKey = botId ? `trades_bot_${botId}` : null;

  // Memoize load function to prevent infinite re-renders
  const load = useCallback(
    (params: Record<string, unknown>) => {
      const parsed = TradeQueryParamsSchema.parse(params);
      return loadTradesByBot(botId, parsed);
    },
    [botId]
  );

  // Memoize error handler to prevent infinite re-renders
  const onLoadError = useCallback((err: unknown) => {
    handleError(getErrorMessage(err, "Trades loading failed"));
  }, []);

  const {
    items: trades,
    refresh,
    isLoading,
    isRefreshing,
    ...other
  } = useCachedStrapiList<Trade>({
    ...props,
    cacheKey,
    load,
    onLoadError,
    depsToReset: [botId],
  });

  return {
    trades,
    refresh,
    isLoading,
    isRefreshing,
    ...other,
  };
};

export const loadTradesByBot = async (botId: number | string | undefined, params?: TradeQueryParams) => {
  if (!botId) return undefined;
  try {
    return await tradesEndpoints.getBotTrades(Number(botId), params);
  } catch (err: unknown) {
    handleError(getErrorMessage(err, "Trades loading failed"));
  }
};

export default useTradesByBot;
